/**
 * The shell every analytics board renders inside, so loading, failure and emptiness look the same
 * on all of them and are written once.
 *
 * The state decision lives in `boardState.ts` as a pure function — this repo has no
 * `@testing-library` and no jsdom, so anything worth testing has to sit outside a component.
 */
import type { ReactNode } from 'react'
import { boardState } from './boardState'

interface BoardCardProps {
  title: string
  /** One line under the title explaining what the board measures. */
  subtitle?: string
  isLoading: boolean
  error: unknown
  isEmpty: boolean
  emptyLabel?: string
  /** Rendered on the right of the header — segment switches and toggles. */
  actions?: ReactNode
  children: ReactNode
}

export function BoardCard({
  title, subtitle, isLoading, error, isEmpty, emptyLabel, actions, children,
}: BoardCardProps) {
  const state = boardState({ isLoading, error, isEmpty })

  return (
    <div className="card pad">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <b>{title}</b>
          {subtitle && (
            <div style={{ color: 'var(--muted)', fontSize: 11.5, marginTop: 2 }}>{subtitle}</div>
          )}
        </div>
        {state === 'ready' ? actions : null}
      </div>

      {state === 'loading' && (
        <div style={{ color: 'var(--muted)', fontSize: 12.5, padding: '20px 0' }}>Loading…</div>
      )}

      {state === 'error' && (
        <div style={{ color: 'var(--danger)', fontSize: 12.5, padding: '20px 0' }}>
          {error instanceof Error ? error.message : 'Could not load this board.'}
        </div>
      )}

      {state === 'empty' && (
        <div style={{ color: 'var(--muted)', fontSize: 12.5, padding: '20px 0' }}>
          {emptyLabel ?? 'No enquiries in this range.'}
        </div>
      )}

      {state === 'ready' && children}
    </div>
  )
}
